import type { HttpClient } from '@wix/http-client'
import {
  addPattern,
  deletePattern,
  getAssetUrlInWorker,
  updatePagePattern,
} from '@wix/wix-data-client-common'
import type {
  PatternDefinition,
  RouterConfig,
} from '@wix/wix-data-client-common'
import { keys } from '@wix/wix-data-client-common-standalone'
import type {
  Logger,
  BackendRouter,
  EditorSDK,
  I18n,
} from '@wix/wix-data-client-common-standalone'
import {
  queryRoutersPublic,
  updateRouterPublic,
  removeRouterPublic,
  addRouterPublic,
} from '@wix/ambassador-document-v1-router/http'
import { omit, uniq } from 'lodash'
import { DATA_BINDING } from '@wix/app-definition-ids'
import { PanelResolveType } from '@wix/editor-platform-sdk-types'

const getRoutersInLiveSite = async ({
  httpClient,
  previousPrefix,
  newPrefix,
}: {
  httpClient: HttpClient
  previousPrefix: string
  newPrefix: string
}): Promise<{
  previousRouter: BackendRouter | undefined
  newRouter: BackendRouter | undefined
}> => {
  const { data } = await httpClient.request(
    queryRoutersPublic({
      query: {
        filter: {
          appId: DATA_BINDING,
          prefix: { $in: uniq([previousPrefix, newPrefix]) },
        },
      },
    }),
  )
  const routers = (data.routers ?? []) as BackendRouter[]
  const previousRouter = routers.find(
    router => router.prefix === previousPrefix,
  )
  const newRouter = routers.find(router => router.prefix === newPrefix)
  return { previousRouter, newRouter }
}

const urlExistsInLiveSite = (
  router: BackendRouter | undefined,
  pattern: string,
): router is BackendRouter => !!router?.config.patterns[pattern]

const previousUrlBelongsToAnotherPageInLiveSite = ({
  previousRouter,
  previousPattern,
  pageId,
}: {
  previousRouter: BackendRouter
  previousPattern: string
  pageId: string
}): boolean => {
  const previousPatternDefinition =
    previousRouter.config.patterns[previousPattern]
  return previousRouter.pages[previousPatternDefinition.pageRole] !== pageId
}

const changePatternInSameRouter = async ({
  httpClient,
  router,
  previousPattern,
  newPattern,
}: {
  httpClient: HttpClient
  router: BackendRouter
  previousPattern: string
  newPattern: string
}): Promise<void> => {
  await httpClient.request(
    updateRouterPublic({
      updateData: {
        router: {
          ...router,
          config: updatePagePattern(router.config, previousPattern, newPattern),
        },
        mask: ['config'],
      },
    }),
  )
}

const removePreviousPatternFromPreviousRouter = async ({
  httpClient,
  previousRouter,
  previousPattern,
}: {
  httpClient: HttpClient
  previousRouter: BackendRouter
  previousPattern: string
}): Promise<void> => {
  const { pageRole } = previousRouter.config.patterns[previousPattern]
  const lastPageInPreviousRouter = keys(previousRouter.pages).length === 1
  const removePatternRequest = lastPageInPreviousRouter
    ? removeRouterPublic({ id: previousRouter.id })
    : updateRouterPublic({
        updateData: {
          router: {
            ...previousRouter,
            config: deletePattern(previousRouter.config, previousPattern),
            pages: omit(previousRouter.pages, pageRole),
          },
          mask: ['config', 'pages'],
        },
      })
  await httpClient.request(removePatternRequest)
}

const getNewRouterConfigWithNewPattern = ({
  patternDefinition,
  previousPattern,
  newPattern,
  configWithoutPattern,
}: {
  patternDefinition: PatternDefinition
  previousPattern: string
  newPattern: string
  configWithoutPattern: RouterConfig
}): RouterConfig => {
  const configWithPreviousPattern = addPattern(
    configWithoutPattern,
    previousPattern,
    patternDefinition,
  )

  const configWithRenamedPattern = updatePagePattern(
    configWithPreviousPattern,
    previousPattern,
    newPattern,
  )
  return configWithRenamedPattern
}

const addNewPatternToNewRouter = async ({
  httpClient,
  previousRouter,
  previousPattern,
  newRouter,
  newPrefix,
  newPattern,
  pageId,
}: {
  httpClient: HttpClient
  previousRouter: BackendRouter
  previousPattern: string
  newRouter?: BackendRouter
  newPrefix: string
  newPattern: string
  pageId: string
}): Promise<void> => {
  const patternDefinition = previousRouter.config.patterns[previousPattern]

  const newRouterConfig = getNewRouterConfigWithNewPattern({
    patternDefinition,
    previousPattern,
    newPattern,
    configWithoutPattern: newRouter ? newRouter.config : { patterns: {} },
  })

  const addPatternRequest = newRouter
    ? updateRouterPublic({
        updateData: {
          router: {
            ...newRouter,
            config: newRouterConfig,
            pages: {
              ...newRouter.pages,
              [patternDefinition.pageRole]: pageId,
            },
          },
          mask: ['config', 'pages'],
        },
      })
    : addRouterPublic({
        appId: DATA_BINDING,
        prefix: newPrefix,
        pages: { [patternDefinition.pageRole]: pageId },
        config: newRouterConfig,
      })
  await httpClient.request(addPatternRequest)
}

const openAskToPublishSitePanel = async ({
  sendSitePagesMenuInteractionsBI,
  editorSdkProxy,
  i18n,
}: {
  sendSitePagesMenuInteractionsBI: (params: {
    panel_name: string
    action: string
  }) => void
  editorSdkProxy: EditorSDK
  i18n: I18n
}): Promise<void> => {
  const panelResult = await editorSdkProxy.editor.openConfirmationPanel({
    headerText: i18n.t(
      'EditDynamicPageUrlStructure_AskToPublishConfirmation_Header',
    ),
    descriptionText: i18n.t(
      'EditDynamicPageUrlStructure_AskToPublishConfirmation_Description',
    ),
    mainActionText: i18n.t(
      'EditDynamicPageUrlStructure_AskToPublishConfirmation_MainActionText',
    ),
    shouldShowIllustration: true,
    illustration: await getAssetUrlInWorker(
      editorSdkProxy,
      'panel-icons/generic_checkmark.svg',
    ),
  })

  const resultToBiAction = {
    [PanelResolveType.MAIN_ACTION]: 'ok',
    [PanelResolveType.CLOSE_ACTION]: 'close',
  }

  if (
    panelResult === PanelResolveType.MAIN_ACTION ||
    panelResult === PanelResolveType.CLOSE_ACTION
  ) {
    sendSitePagesMenuInteractionsBI({
      panel_name: 'url-change-publish-reminder-panel',
      action: resultToBiAction[panelResult],
    })
  }
}

export const saveUrlInLiveSite = async ({
  httpClient,
  editorSdkProxy,
  i18n,
  logger,
  pageId,
  previousPrefix,
  previousPattern,
  newPrefix,
  newPattern,
  sendSitePagesMenuInteractionsBI,
}: {
  httpClient: HttpClient
  editorSdkProxy: EditorSDK
  i18n: I18n
  logger: Logger
  pageId: string
  previousPrefix: string
  previousPattern: string
  newPrefix: string
  newPattern: string
  sendSitePagesMenuInteractionsBI: (params: {
    panel_name: string
    action: string
  }) => void
}): Promise<void> => {
  try {
    const { previousRouter, newRouter } = await getRoutersInLiveSite({
      httpClient,
      previousPrefix,
      newPrefix,
    })

    if (!urlExistsInLiveSite(previousRouter, previousPattern)) {
      return
    }

    if (
      urlExistsInLiveSite(newRouter, newPattern) ||
      previousUrlBelongsToAnotherPageInLiveSite({
        previousRouter,
        previousPattern,
        pageId,
      })
    ) {
      openAskToPublishSitePanel({
        sendSitePagesMenuInteractionsBI,
        editorSdkProxy,
        i18n,
      })
      return
    }

    if (previousPrefix === newPrefix) {
      await changePatternInSameRouter({
        httpClient,
        router: previousRouter,
        previousPattern,
        newPattern,
      })
    } else {
      await removePreviousPatternFromPreviousRouter({
        httpClient,
        previousRouter,
        previousPattern,
      })
      await addNewPatternToNewRouter({
        httpClient,
        previousRouter,
        previousPattern,
        newRouter,
        newPrefix,
        newPattern,
        pageId,
      })
    }
  } catch (error) {
    logger.error(
      new Error(
        'Failed to update live site routers with new dynamic page url',
        {
          cause: error,
        },
      ),
    )
    openAskToPublishSitePanel({
      sendSitePagesMenuInteractionsBI,
      editorSdkProxy,
      i18n,
    })
  }
}
